export interface PaginatedResponse<T> {
  content: T[]
  empty: boolean
  first: boolean
  last: boolean
  number: number
  numberOfElements: number
  size: number
  sort: {
    sorted: boolean
    unsorted: boolean
    empty: boolean
  }
  totalElements: number
  totalPages: number
}

/**
 * Reads the CSRF token from the XSRF-TOKEN cookie
 * @returns The CSRF token value or undefined if not found
 */
export const getCsrfToken = (): string | undefined => {
  return document.cookie
    .split("; ")
    .find((row) => row.startsWith("XSRF-TOKEN="))
    ?.split("=")[1]
}

/**
 * Creates headers for API requests
 * @param token JWT authorization token
 * @param method HTTP method (optional) - if POST/PUT/DELETE, includes CSRF token
 * @returns Headers object
 */
export const mkHeaders = (token: string, method?: string) => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }

  // Add CSRF token for state-changing operations
  if (method && ["POST", "PUT", "DELETE"].includes(method.toUpperCase())) {
    const csrfToken = getCsrfToken()
    if (csrfToken) {
      headers["X-XSRF-TOKEN"] = csrfToken
    }
  }

  return headers
}

export class UnauthorisedError extends Error {
  constructor(message: string) {
    super(message)
  }
}

/**
 * API error with structured context for better observability
 */
export class ApiError extends Error {
  readonly statusCode: number
  readonly operation: string
  readonly context: Record<string, string | undefined>

  constructor(
    message: string,
    statusCode: number,
    operation: string,
    context: Record<string, string | undefined> = {},
  ) {
    super(message)
    this.name = "ApiError"
    this.statusCode = statusCode
    this.operation = operation
    this.context = context
  }
}
